import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import ErrorComponent from './error/ErrorComponent';
import { HOST } from '../config/api';
import { setAuthCookies } from '../lib/auth';
import { clearStoredShareInvite, getStoredShareInvite, storeShareInvite } from '../lib/shareInvite';

const Login = ({ setUserRole, setUserId }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isForgotPasswordSubmitting, setIsForgotPasswordSubmitting] = useState(false);
    const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
    const [forgotPasswordErrorMessage, setForgotPasswordErrorMessage] = useState('');
    const [inviteDismissed, setInviteDismissed] = useState(false);

    const location = useLocation();
    const routeShareInvite = location.state?.shareInvite || null;
    const shareInvite = useMemo(() => routeShareInvite || getStoredShareInvite(), [routeShareInvite]);
    const activeShareInvite = inviteDismissed ? null : shareInvite;

    useEffect(() => {
        if (routeShareInvite?.code) {
            storeShareInvite(routeShareInvite);
            setInviteDismissed(false);
        }
    }, [routeShareInvite]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoginErrorMessage(null);
        setIsSubmitting(true);

        const payload = {
            email,
            password,
        };

        try {
            const response = await fetch(HOST + '/v1/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok) {
                const user = result.user;
                setAuthCookies(user.token, user.userId);

                setUserRole(user.userRole === 99 ? 1 : 2);
                setUserId(user.userId);
                if (activeShareInvite?.code) {
                    navigate(`/s/${activeShareInvite.code}`, { replace: true });
                    return;
                }

                navigate('/');
            } else {
                setLoginErrorMessage(result.message);
            }
        } catch (error) {
            setLoginErrorMessage('Login failed. Please check your connection and try again.');
            console.error('Error during login request:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        setForgotPasswordMessage('');
        setForgotPasswordErrorMessage('');

        if (!email.trim()) {
            setForgotPasswordErrorMessage('Enter your email so we can send the reset link.');
            return;
        }

        setIsForgotPasswordSubmitting(true);

        try {
            const response = await fetch(HOST + '/v1/forgot_password?email=' + encodeURIComponent(email.trim()), {
                method: 'GET',
            });

            if (!response.ok) {
                setForgotPasswordErrorMessage('We could not send the reset email. Please try again in a moment.');
                return;
            }

            setForgotPasswordSent(true);
            setForgotPasswordMessage('Please check your email to reset your password.');
        } catch (error) {
            setForgotPasswordErrorMessage('We could not send the reset email. Please check your connection and try again.');
            console.error('Error during forgot password request:', error);
        } finally {
            setIsForgotPasswordSubmitting(false);
        }
    };

    const dismissInvite = () => {
        clearStoredShareInvite();
        setInviteDismissed(true);
    };

    const title = isForgotPassword ? 'Reset your password' : 'Welcome back';
    const subtitle = isForgotPassword
        ? 'Enter your email and we will send you a reset link.'
        : 'Sign in to return to your workspace and continue your training flow.';

    return (
        <div className={styles.authPage}>
            <section className={styles.authShell}>
                <div className={styles.authIntro}>
                    <div className={styles.authImageWrap}>
                        <img src="/black_and_white_image.png" alt="Underwater training" className={styles.authImage} />
                        <div className={styles.authImageOverlay}>
                            <span>Lead the Way</span>
                            <strong>Helping you achieve perfection.</strong>
                            <p>One focused system for coaches, athletes, and the work between them.</p>
                        </div>
                    </div>

                    <div className={styles.authCopy}>
                        <span className={styles.authEyebrow}>Account access</span>
                        <h1>{isForgotPassword ? 'Recover your access without losing momentum.' : 'Return to your training rhythm.'}</h1>
                        <p>
                            {isForgotPassword
                                ? 'We keep the reset step simple so you can get back into the product quickly.'
                                : 'Your plans, progress, subscriptions, and sessions are waiting in one clean workspace.'}
                        </p>
                    </div>
                </div>

                <div className={styles.authPanel}>
                    <div className={styles.panelHeader}>
                        <span className={styles.panelKicker}>{isForgotPassword ? 'Password help' : 'Login'}</span>
                        <h2>{title}</h2>
                        <p>{subtitle}</p>
                    </div>

                    {activeShareInvite?.message && !isForgotPassword ? (
                        <div className={styles.inviteNotice} role="status" aria-live="polite">
                            <span className={styles.inviteNoticeKicker}>Training invite</span>
                            <p>{activeShareInvite.message}</p>
                            <button type="button" className={styles.inlineLink} onClick={dismissInvite}>
                                Continue without this invite
                            </button>
                        </div>
                    ) : null}

                    <form className={styles.authForm} onSubmit={isForgotPassword ? handleForgotPasswordSubmit : handleSubmit}>
                        <div className={styles.formGroup}>
                            <label className={styles.fieldLabel} htmlFor={isForgotPassword ? 'forgotEmail' : 'email'}>
                                Email
                            </label>
                            <input
                                type="email"
                                id={isForgotPassword ? 'forgotEmail' : 'email'}
                                className={styles.inputField}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                            />
                        </div>

                        {!isForgotPassword && (
                            <div className={styles.formGroup}>
                                <label className={styles.fieldLabel} htmlFor="password">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    className={styles.inputField}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                />
                            </div>
                        )}

                        <ErrorComponent errorMessage={loginErrorMessage} />

                        {forgotPasswordSent && forgotPasswordMessage ? (
                            <p className={styles.registrationMessageSuccess}>
                                {forgotPasswordMessage}
                            </p>
                        ) : null}

                        {forgotPasswordErrorMessage ? (
                            <p className={styles.registrationMessageFailure}>{forgotPasswordErrorMessage}</p>
                        ) : null}

                        <div className={styles.actionStack}>
                            {!forgotPasswordSent && (
                                <button type="submit" className={styles.primaryButton} disabled={isSubmitting || isForgotPasswordSubmitting}>
                                    {isForgotPassword
                                        ? (isForgotPasswordSubmitting ? 'Sending reset link...' : 'Send reset link')
                                        : (isSubmitting ? 'Logging in...' : 'Login')}
                                </button>
                            )}

                            {isForgotPassword ? (
                                <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={() => {
                                        setForgotPasswordSent(false);
                                        setIsForgotPassword(false);
                                        setForgotPasswordMessage('');
                                        setForgotPasswordErrorMessage('');
                                    }}
                                    disabled={isForgotPasswordSubmitting}
                                >
                                    Back to login
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={() => navigate('/register', { state: activeShareInvite ? { shareInvite: activeShareInvite } : undefined })}
                                    disabled={isSubmitting}
                                >
                                    Create account
                                </button>
                            )}
                        </div>

                        {!isForgotPassword && (
                            <button
                                type="button"
                                onClick={() => {
                                    setForgotPasswordSent(false);
                                    setForgotPasswordMessage('');
                                    setForgotPasswordErrorMessage('');
                                    setLoginErrorMessage(null);
                                    setIsForgotPassword(true);
                                }}
                                className={styles.inlineLink}
                                disabled={isSubmitting}
                            >
                                Forgot Password?
                            </button>
                        )}
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Login;
